import { defineComponent as _defineComponent } from 'vue'
import { normalizeStyle as _normalizeStyle, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, vModelText as _vModelText, withDirectives as _withDirectives, toDisplayString as _toDisplayString } from "vue"

const _hoisted_1 = { class: "row1" }
const _hoisted_2 = {
  key: 0,
  class: "play-speed-control"
}
const _hoisted_3 = { class: "speed-btn-container" }
const _hoisted_4 = ["onClick"]
const _hoisted_5 = { class: "playback-control" }
const _hoisted_6 = {
  key: 0,
  src: '/static/img/1s_foward.png',
  class: "sec-control-btn"
}
const _hoisted_7 = {
  key: 0,
  src: '/static/img/60s_foward.png',
  class: "sec-control-btn"
}
const _hoisted_8 = {
  key: 1,
  class: "volume-control"
}
const _hoisted_9 = { class: "ib vm bar" }
const _hoisted_10 = { class: "custom-control custom-checkbox backward-checkbox" }
const _hoisted_11 = ["id", "checked"]
const _hoisted_12 = ["for"]
const _hoisted_13 = {
  key: 1,
  class: "row2"
}
const _hoisted_14 = { class: "seek-bar" }
const _hoisted_15 = { class: "lbl time-start" }
const _hoisted_16 = ["value", "min", "max", "step"]
const _hoisted_17 = { class: "lbl time-end" }

import {
  reactive,
  computed,
  ref,
} from 'vue';
import type { ViewMode, SizeMode } from 'src/models/moviePlayer';

interface VolumeState {
  val: number;
}
interface MoviePlayerControlAreaState {
  volume: VolumeState;
}

// withDefaultsでdefaultを与えても、TS上optional(?)でないとrequired:trueのままになるVueの仕様
// https://github.com/vuejs/core/blob/v3.5.35/packages/compiler-sfc/src/script/resolveType.ts (required: !e.optional)
// controlAreaId/viewModeは全呼び出し元が渡しているため必須のまま。isPlayBackward/isPlayingはdefaultなしのため必須のまま
interface Props {
  controlAreaId: string;
  viewMode: ViewMode;
  sizeMode?: SizeMode;
  currentTimeDisp?: string;
  startTimeDisp?: string;
  endTimeDisp?: string;
  seekMin?: number;
  seekMax?: number;
  seekStep?: number;
  seekCurrent?: number;
  cornerLabelOpacity?: number;
  playSpeed?: number;
  isPlayBackward: boolean;
  isPlaying: boolean;
}

const volumeMin = 0.0;
const volumeMax = 1.0;
const volumeStep = 0.01;
const volumeEmitDenom = 100;


export default /*@__PURE__*/_defineComponent({
  __name: 'index',
  props: {
    controlAreaId: {},
    viewMode: {},
    sizeMode: { default: 'lg' },
    currentTimeDisp: { default: '' },
    startTimeDisp: { default: '' },
    endTimeDisp: { default: '' },
    seekMin: { default: 0 },
    seekMax: { default: 0 },
    seekStep: { default: 1 },
    seekCurrent: { default: 0 },
    cornerLabelOpacity: { default: 1.0 },
    playSpeed: { default: 1.0 },
    isPlayBackward: { type: Boolean },
    isPlaying: { type: Boolean }
  },
  emits: ["play", "pause", "tick-backward", "tick-forward", "seek-change", "play-previous-movie", "play-next-movie", "set-play-speed", "set-volume", "playmode-change"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const props = __props;

const emit = __emit;

const playSpeeds = [0.25, 0.5, 1.0, 2.0, 5.0];
const initVolumeState = (): VolumeState => {
  return {
    val: 1.0,
  };
};
const state = reactive<MoviePlayerControlAreaState>({
  volume: initVolumeState(),
});
const refSeekbarInput = ref<HTMLInputElement>();

// computed
const isViewModeFull = computed(() => {
  return props.viewMode === 'full';
});
const isViewModeComp = computed(() => {
  return props.viewMode === 'comp';
});
const isViewModeOther = computed(() => {
  return !isViewModeFull.value && !isViewModeComp.value;
});
const isSizeModeLg = computed(() => {
  return props.sizeMode === 'lg';
});
const isSizeModeGteLg = computed(() => {
  return isSizeModeLg.value;
});
const isPaused = computed(() => {
  return !props.isPlaying;
});
const myCurrentTimeDisp = computed(() => {
  // currentがまだ来てなかったらstartの方を表示しとく
  return props.currentTimeDisp || props.startTimeDisp;
});

// methods
const play = () => {
  emit('play');
};
const pause = () => {
  emit('pause');
};
const tickBackward = () => {
  emit('tick-backward');
};
const tickForward = () => {
  emit('tick-forward');
};
const emitSeekChange = (msec: number) => {
  if (!refSeekbarInput.value) return;

  const currentMSec = parseInt(refSeekbarInput.value.value);
  emit('seek-change', currentMSec + msec);
};
const seekBackwardOneSec = () => {
  emitSeekChange(-1000);
};
const seekBackwardSixtySec = () => {
  emitSeekChange(-60000);
};
const seekForwardOneSec = () => {
  emitSeekChange(1000);
};
const seekForwardSixtySec = () => {
  emitSeekChange(60000);
};
const changePlaySpeed = (speed: number) => {
  emit('set-play-speed', speed);
};
const getVolumeEmitVal = () => {
  return state.volume.val / volumeEmitDenom;
};
const volumeOff = () => {
  state.volume.val = volumeMin;
  emit('set-volume', getVolumeEmitVal());
};
const volumeUp = () => {
  state.volume.val = Math.min(
    state.volume.val + volumeStep,
    volumeMax,
  );
  emit('set-volume', getVolumeEmitVal());
};
const volumeChange = () => {
  emit('set-volume', getVolumeEmitVal());
};
const seekChange = (evt: InputEvent) => {
  if (!evt.target) return;

  emit('seek-change', parseInt((evt.target as HTMLInputElement).value));
};
const resetVolume = () => {
  state.volume.val = volumeMin;
};
const onIsPlayBackwardChange = (evt: Event) => {
  const isPlayBackward = (evt.target as HTMLInputElement).checked;
  emit('playmode-change', isPlayBackward);
};

__expose({ emitSeekChange, resetVolume });

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["movie-player-control-area", {
      'full-mode': isViewModeFull.value,
      'compare-mode': isViewModeComp.value,
    }])
  }, [
    (isViewModeComp.value)
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          class: "corner-triangle-label",
          style: _normalizeStyle({opacity: __props.cornerLabelOpacity})
        }, null, 4))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_1, [
      (isSizeModeGteLg.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
            _cache[3] || (_cache[3] = _createElementVNode("div", { class: "lbl-container" }, [
              _createElementVNode("label", { class: "lbl" }, "再生速度")
            ], -1)),
            _createElementVNode("div", _hoisted_3, [
              (_openBlock(), _createElementBlock(_Fragment, null, _renderList(playSpeeds, (e, i) => {
                return _createElementVNode("div", {
                  class: _normalizeClass(["btn btn-secondary speed-btn", { active: e === __props.playSpeed }]),
                  key: i,
                  onClick: ($event: any) => (changePlaySpeed(e))
                }, null, 10, _hoisted_4)
              }), 64))
            ])
          ]))
        : _createCommentVNode("", true),
      _createElementVNode("div", _hoisted_5, [
        (!isViewModeOther.value)
          ? (_openBlock(), _createElementBlock("div", {
              key: 0,
              class: "ib vm control-btn",
              onClick: seekBackwardSixtySec
            }, [...(_cache[4] || (_cache[4] = [
              _createElementVNode("img", {
                src: '/static/img/60s_back.png',
                class: "sec-control-btn"
              }, null, -1)
            ]))]))
          : _createCommentVNode("", true),
        (!isViewModeOther.value)
          ? (_openBlock(), _createElementBlock("div", {
              key: 1,
              class: "ib vm control-btn",
              onClick: seekBackwardOneSec
            }, [...(_cache[5] || (_cache[5] = [
              _createElementVNode("img", {
                src: '/static/img/1s_back.png',
                class: "sec-control-btn"
              }, null, -1)
            ]))]))
          : _createCommentVNode("", true),
        _createElementVNode("div", {
          class: "ib vm control-btn",
          onClick: tickBackward
        }, [...(_cache[6] || (_cache[6] = [
          _createElementVNode("i", { class: "fas fa-backward" }, null, -1)
        ]))]),
        (isPaused.value)
          ? (_openBlock(), _createElementBlock("div", {
              key: 2,
              class: "ib vm control-btn",
              onClick: play
            }, [
              _createElementVNode("i", {
                class: _normalizeClass(["fa-2x far fa-play-circle", {'play-backward': __props.isPlayBackward}])
              }, null, 2)
            ]))
          : _createCommentVNode("", true),
        (__props.isPlaying)
          ? (_openBlock(), _createElementBlock("div", {
              key: 3,
              class: "ib vm control-btn pause",
              onClick: pause
            }, [...(_cache[7] || (_cache[7] = [
              _createElementVNode("i", { class: "fa-2x far fa-pause-circle" }, null, -1)
            ]))]))
          : _createCommentVNode("", true),
        _createElementVNode("div", {
          class: "ib vm control-btn",
          onClick: tickForward
        }, [...(_cache[8] || (_cache[8] = [
          _createElementVNode("i", { class: "fas fa-forward" }, null, -1)
        ]))]),
        _createElementVNode("div", {
          class: "ib vm control-btn",
          onClick: seekForwardOneSec
        }, [
          (!isViewModeOther.value)
            ? (_openBlock(), _createElementBlock("img", _hoisted_6))
            : _createCommentVNode("", true)
        ]),
        _createElementVNode("div", {
          class: "ib vm control-btn",
          onClick: seekForwardSixtySec
        }, [
          (!isViewModeOther.value)
            ? (_openBlock(), _createElementBlock("img", _hoisted_7))
            : _createCommentVNode("", true)
        ])
      ]),
      (isSizeModeGteLg.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
            _createElementVNode("div", {
              class: "ib vm lbl volume-off",
              onClick: volumeOff
            }, [...(_cache[9] || (_cache[9] = [
              _createElementVNode("i", { class: "fas fa-volume-off" }, null, -1)
            ]))]),
            _createElementVNode("div", _hoisted_9, [
              _withDirectives(_createElementVNode("input", {
                type: "range",
                class: "slider",
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((state.volume.val) = $event)),
                onChange: volumeChange,
                min: volumeMin,
                max: volumeMax,
                step: volumeStep
              }, null, 544), [
                [
                  _vModelText,
                  state.volume.val,
                  void 0,
                  { number: true }
                ]
              ])
            ]),
            _createElementVNode("div", {
              class: "ib vm lbl volume-up",
              onClick: volumeUp
            }, [...(_cache[10] || (_cache[10] = [
              _createElementVNode("i", { class: "fas fa-volume-up" }, null, -1)
            ]))])
          ]))
        : _createCommentVNode("", true),
      _createElementVNode("div", _hoisted_10, [
        _createElementVNode("input", {
          type: "checkbox",
          class: "custom-control-input",
          id: `is-playbackward-${__props.controlAreaId}`,
          onClick: pause,
          checked: __props.isPlayBackward,
          onChange: _cache[1] || (_cache[1] = ($event: any) => (onIsPlayBackwardChange($event)))
        }, null, 40, _hoisted_11),
        _createElementVNode("label", {
          class: "custom-control-label",
          for: `is-playbackward-${__props.controlAreaId}`
        }, " 逆再生 ", 8, _hoisted_12)
      ])
    ]),
    (!isViewModeOther.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
          _createElementVNode("div", _hoisted_14, [
            _createElementVNode("span", _hoisted_15, _toDisplayString(myCurrentTimeDisp.value), 1),
            _createElementVNode("input", {
              type: "range",
              class: "slider",
              value: __props.seekCurrent,
              onInput: _cache[2] || (_cache[2] = ($event: any) => (seekChange($event))),
              min: __props.seekMin,
              max: __props.seekMax,
              step: __props.seekStep,
              ref_key: "refSeekbarInput",
              ref: refSeekbarInput
            }, null, 40, _hoisted_16),
            _createElementVNode("span", _hoisted_17, _toDisplayString(__props.endTimeDisp), 1)
          ])
        ]))
      : _createCommentVNode("", true)
  ], 2))
}
}

})